import { ApiClientBase } from '@@/api/api';
import { ApiResult } from '@@/types/ApiResult';
import { baseApiResponse, RequestBase } from '@@/api/api-common';
import { LanguageCode, VoiceId } from '@aws-sdk/client-polly';
import z from 'zod';

export enum Engine {
  STANDARD = 'standard',
  NEURAL = 'neural',
  LONG_FORM = 'long-form',
  GENERATIVE = 'generative'
}

export interface PollySynthesizePayload {
  voiceId: VoiceId;
  content: string;
  textType: 'text' | 'ssml';
  languageCode: LanguageCode;
  engine: Engine;
}

export interface PollySynthesizeResponse extends baseApiResponse {
  audioContent: string;
  contentType: string;
}

export interface BulkExportBody {
  baseEntityId: string;
}
export interface BulkExportResponse extends baseApiResponse {
  itemCount: number;
  entityCount: number;
  url: string;
}

const BulkImportBodySchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('get_presigned_url'),
    baseEntityId: z.string().min(1).max(64),
    contentLength: z.number().min(1).max(1048576000)
  }),
  z.object({
    action: z.literal('start_import'),
    objectKey: z.string().min(1).max(2048),
    baseEntityId: z.string().min(1).max(64)
  })
]);
type BulkImportBodySchemaType = z.infer<typeof BulkImportBodySchema>;

interface BulkImportPresignedUrlResponse extends baseApiResponse {
  message: string;
  url: string;
  objectKey: string;
}

export interface ImportError {
  entityId: string;
  itemId?: string;
  fieldName: string;
  reason: string;
  details: Record<string, unknown>;
}

export interface BulkImportStartResponse extends baseApiResponse {
  message: string;
  lines: number;
  importedEntityCount: number;
  importedItemCount: number;
  unprocessedEntityCount: number;
  unprocessedItemCount: number;
  errorCount: number;
  errors: ImportError[];
}

type BulkImportResponse = BulkImportPresignedUrlResponse | BulkImportStartResponse;

export class ToolsClient extends ApiClientBase {
  async pollySynthesize({
    input,
    queryString
  }: RequestBase<PollySynthesizePayload>): Promise<ApiResult<PollySynthesizeResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/tools/polly?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.message ?? 'Failed to synthesize speech');
      }

      const response = await result.json();

      return response;
    } catch (error) {
      return this.error(`Failed to synthesize speech: ${(error as Error).message}`);
    }
  }

  async bulkExport({ input, queryString }: RequestBase<BulkExportBody>): Promise<ApiResult<BulkExportResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/tools/bulk-export?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.message ?? 'Failed to export data');
      }

      const response = await result.json();

      return response;
    } catch (error) {
      return this.error(`Failed to export data: ${(error as Error).message}`);
    }
  }

  async bulkImport({
    input,
    queryString
  }: RequestBase<BulkImportBodySchemaType>): Promise<ApiResult<BulkImportResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/tools/bulk-import?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.message ?? 'Failed to import data');
      }

      const response = await result.json();

      return response;
    } catch (error) {
      return this.error(`Failed to import data: ${(error as Error).message}`);
    }
  }
}
